import React from 'react';
import { UISchema, UIComponentProps } from '@types/index';

export type ComponentRenderer = React.FC<UIComponentProps & { children?: React.ReactNode }>;

class UIComponentRegistry {
  private components: Map<string, ComponentRenderer> = new Map();

  register(type: string, component: ComponentRenderer): void {
    this.components.set(type.toLowerCase(), component);
  }

  get(type: string): ComponentRenderer | undefined {
    return this.components.get(type.toLowerCase());
  }

  has(type: string): boolean {
    return this.components.has(type.toLowerCase());
  }

  getAll(): Map<string, ComponentRenderer> {
    return new Map(this.components);
  }

  clear(): void {
    this.components.clear();
  }
}

export const componentRegistry = new UIComponentRegistry();

export interface SchemaValidationResult {
  valid: boolean;
  errors: string[];
}

export class SchemaValidator {
  validate(schema: UISchema): SchemaValidationResult {
    const errors: string[] = [];

    if (!schema.type) {
      errors.push('Schema must have a type property');
    }

    if (schema.children) {
      if (!Array.isArray(schema.children)) {
        errors.push('Children must be an array');
      } else {
        schema.children.forEach((child, index) => {
          const childValidation = this.validate(child);
          if (!childValidation.valid) {
            errors.push(`Child at index ${index}: ${childValidation.errors.join(', ')}`);
          }
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

export const schemaValidator = new SchemaValidator();

export interface EvaluationContext {
  data?: Record<string, any>;
  functions?: Record<string, Function>;
  [key: string]: any;
}

export class ExpressionEvaluator {
  evaluate(expression: string, context: EvaluationContext = {}): any {
    try {
      // Simple expression evaluation
      // For production, consider using a library like expr-eval or jsep
      const safeContext = {
        ...context,
        Math,
        Date,
        JSON,
      };

      const func = new Function(...Object.keys(safeContext), `return ${expression}`);
      return func(...Object.values(safeContext));
    } catch (error) {
      console.error('Expression evaluation error:', error);
      return null;
    }
  }

  evaluateCondition(condition: string, context: EvaluationContext): boolean {
    try {
      return Boolean(this.evaluate(condition, context));
    } catch (error) {
      console.error('Condition evaluation error:', error);
      return true;
    }
  }
}

export const expressionEvaluator = new ExpressionEvaluator();

export class TemplateInterpolator {
  interpolate(template: string, data: Record<string, any>): string {
    return template.replace(/\{\{(.*?)\}\}/g, (match, key) => {
      const value = this.getNestedValue(key.trim(), data);
      return String(value !== undefined ? value : match);
    });
  }

  private getNestedValue(path: string, obj: Record<string, any>): any {
    return path.split('.').reduce((current, part) => current?.[part], obj);
  }
}

export const templateInterpolator = new TemplateInterpolator();
